'use client';

import { BillingCycle, Currency } from '@prezly/sdk';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

import { AGENCY_PRICING_TABLE, STANDARD_PRICING_TABLE } from './pricing';
import type { PricingTable } from './types';
import { TableId } from './types';

interface Context {
    currency: Currency.EUR | Currency.USD;
    billingCycle: BillingCycle;
    pricingTable: PricingTable;
    pricingPlanType: `${TableId}`;
    toggleCurrency: () => void;
    toggleBillingCycle: () => void;
}

const PricingPageContext = createContext<Context>({
    currency: Currency.EUR,
    billingCycle: BillingCycle.YEAR,
    pricingPlanType: TableId.STANDARD,
    toggleCurrency: () => {},
    toggleBillingCycle: () => {},
    pricingTable: { id: TableId.STANDARD, options: [], rows: [] },
});

interface Props {
    variant: `${TableId}`;
    children: ReactNode;
}

function PricingPageContextProvider({ variant, children }: Props) {
    const [currency, setCurrency] = useState<Context['currency']>(Currency.EUR);
    const [billingCycle, setBillingCycle] = useState<BillingCycle>(BillingCycle.YEAR);

    useEffect(() => {
        async function detectCountryCurrency() {
            const response = await fetch('/api/get-currency');
            if (response.ok) {
                const payload = (await response.json()) as { currency: Currency };
                setCurrency(payload.currency === Currency.EUR ? Currency.EUR : Currency.USD);
            }
        }

        detectCountryCurrency();
    }, []);

    function toggleCurrency() {
        setCurrency(currency === Currency.EUR ? Currency.USD : Currency.EUR);
    }

    function toggleBillingCycle() {
        setBillingCycle(
            billingCycle === BillingCycle.MONTH ? BillingCycle.YEAR : BillingCycle.MONTH,
        );
    }

    return (
        <PricingPageContext.Provider
            value={{
                currency,
                toggleCurrency,
                billingCycle,
                toggleBillingCycle,
                pricingTable:
                    variant === TableId.STANDARD ? STANDARD_PRICING_TABLE : AGENCY_PRICING_TABLE,
                pricingPlanType: variant,
            }}
        >
            {children}
        </PricingPageContext.Provider>
    );
}

const usePricingPageContext = () => useContext(PricingPageContext);

export { usePricingPageContext, PricingPageContextProvider };
